import { useState, useEffect, useRef } from 'react';
import { useSearchDrama } from '@hook/useApi';
import { useNavigate } from 'react-router-dom';
import { getCoverImageUrl, isMobile } from '@utils/helpers';
import { debounce } from '@utils/helpers';
import './SearchOverlay.css';

function SearchOverlay({ isOpen, onClose, query, onQueryChange }) {
    const navigate = useNavigate();
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [isMobileView, setIsMobileView] = useState(isMobile());
    const inputRef = useRef(null);

    // Auto-focus on mobile when opened
    useEffect(() => {
        if (isOpen && isMobileView && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    }, [isOpen, isMobileView]);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(isMobile());
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Debounce search query
    useEffect(() => {
        const handler = debounce(() => {
            setDebouncedQuery(query);
        }, 300);

        handler();

        return () => clearTimeout(handler);
    }, [query]);

    const { books, isLoading } = useSearchDrama(debouncedQuery, 1, 20);

    const handleDramaClick = (drama) => {
        const mobileParam = isMobileView ? '&mobile=1' : '';
        navigate(`/player?bookId=${drama.id}&title=${encodeURIComponent(drama.name)}${mobileParam}`);
        onQueryChange(''); // Clear query
        onClose();
    };

    const handleClearSearch = () => {
        onQueryChange('');
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="search-overlay-new"
            onClick={(e) => {
                if (e.target.className === 'search-overlay-new') onClose();
            }}
        >
            <div className="search-modal">
                {/* Search Header */}
                <div className="search-header">
                    <div className="search-input-wrapper">
                        <i className="fas fa-search search-icon"></i>
                        <input
                            ref={inputRef}
                            type="text"
                            className="search-input-field"
                            placeholder="Cari drama favorit..."
                            value={query}
                            onChange={(e) => onQueryChange(e.target.value)}
                            autoComplete="off"
                            autoFocus={!isMobileView}
                        />
                        {query && (
                            <button
                                className="search-clear-btn"
                                onClick={handleClearSearch}
                                aria-label="Clear"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        )}
                    </div>
                    <button
                        className="search-close-btn"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                {/* Search Results */}
                <div className="search-results-container">
                    {isLoading ? (
                        <div className="search-loading">
                            <div className="search-loader">
                                <i className="fas fa-circle-notch fa-spin"></i>
                            </div>
                            <p>Mencari drama...</p>
                        </div>
                    ) : books && books.length > 0 ? (
                        <>
                            <div className="search-results-header">
                                <span className="search-results-count">
                                    {books.length} hasil ditemukan
                                </span>
                            </div>
                            <div className="search-results-grid">
                                {books.map((drama) => (
                                    <div
                                        key={drama.id}
                                        className="search-result-card"
                                        onClick={() => handleDramaClick(drama)}
                                    >
                                        <div className="search-result-poster">
                                            <img
                                                src={getCoverImageUrl(drama)}
                                                alt={drama.name}
                                                loading="lazy"
                                                onError={(e) => {
                                                    e.target.src = 'https://via.placeholder.com/300x400/1a1a1a/666?text=No+Image';
                                                }}
                                            />
                                            <div className="search-result-overlay">
                                                <i className="fas fa-play-circle"></i>
                                            </div>
                                        </div>
                                        <div className="search-result-content">
                                            <h4 className="search-result-title">{drama.name}</h4>
                                            <p className="search-result-episodes">
                                                {drama.chapterCount || 0} Episode
                                            </p>
                                            {drama.introduction && (
                                                <p className="search-result-desc">
                                                    {drama.introduction.length > 80
                                                        ? `${drama.introduction.substring(0, 80)}...`
                                                        : drama.introduction}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : query ? (
                        <div className="search-empty">
                            <div className="search-empty-icon">
                                <i className="fas fa-search"></i>
                            </div>
                            <h3>Tidak ada hasil</h3>
                            <p>Tidak ada drama yang ditemukan untuk "{query}"</p>
                            <button
                                className="search-empty-btn"
                                onClick={handleClearSearch}
                            >
                                Coba kata kunci lain
                            </button>
                        </div>
                    ) : (
                        <div className="search-empty">
                            <div className="search-empty-icon">
                                <i className="fas fa-film"></i>
                            </div>
                            <h3>Cari Drama Favorit</h3>
                            <p>Ketik judul drama yang ingin Anda tonton</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SearchOverlay;
